import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Circle, Eye, Upload, ShieldCheck, Printer, X, FolderOpen, Download, Pencil, Lock, Send, Clock } from "lucide-react";
import { ReplaceRequestButton, ReplacementNote, RejectRequestButton, RejectedNote, RequestDocButton, RequestMissingButton } from "../../components/ReplaceRequestButton";
import { BankVerifiedPill, KycBadge } from "../../components/KycBadges";
import { ReviewActions, ReviewBadge, StageBadge } from "../../components/ReviewActions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API, withCredentials: true });
const fmtErr = (d) => (typeof d === "string" ? d : d?.msg || "Something went wrong.");
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");
const fmtDT = (d) => (d ? new Date(d).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "—");

const STATUS_BADGE = {
  uploaded: "text-emerald-300 border-emerald-500/50",
  provided: "text-emerald-300 border-emerald-500/50",
  acknowledged: "text-emerald-300 border-emerald-500/50",
  pending: "text-slate-400 border-white/20",
  rejected: "text-red-300 border-red-500/50 bg-red-500/10",
};

export default function AdminCandidateChecklist({ letterId, onClose, isCeo = false }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/candidate-docs/admin/${letterId}`);
      setData(data);
    } catch (err) {
      toast.error(fmtErr(err?.response?.data?.detail));
    }
  }, [letterId]);

  useEffect(() => { load(); }, [load]);

  const viewFile = async (itemId) => {
    setBusy(itemId + "view");
    try {
      const res = await api.get(`/candidate-docs/admin/${letterId}/file/${itemId}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("Could not open file"); } finally { setBusy(""); }
  };

  const verify = async (itemId, verified) => {
    setBusy(itemId + "verify");
    try {
      await api.post(`/candidate-docs/admin/${letterId}/verify`, { item_id: itemId, verified });
      await load();
    } catch (err) { toast.error(fmtErr(err?.response?.data?.detail)); } finally { setBusy(""); }
  };

  const hrUpload = async (itemId, file) => {
    if (!file) return;
    setBusy(itemId + "up");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/candidate-docs/admin/${letterId}/upload/${itemId}`, fd);
      toast.success("Uploaded");
      await load();
    } catch (err) { toast.error(fmtErr(err?.response?.data?.detail)); } finally { setBusy(""); }
  };

  const requestDoc = async (itemId, label, reason) => {
    setBusy(itemId + "req");
    try {
      const { data: r } = await api.post(`/candidate-review/admin/${letterId}/request-doc`, { item_id: itemId, reason });
      const t = r.telegram;
      toast.success(`${label} requested — emailed ${r.doc_request?.sent_to}, link valid ${r.doc_request?.window}${t?.ok ? ` · CEO informed on Telegram (${t.sent.length} chats)` : ""}`);
      if (t && !t.ok) toast.warning("Telegram confirmation NOT delivered to the CEO — check CEO_TELEGRAM_CHAT_IDS.", { duration: 9000 });
      await load();
    } catch (err) { toast.error(fmtErr(err?.response?.data?.detail)); throw err; } finally { setBusy(""); }
  };

  const requestReplacement = async (itemId, reason) => {
    try {
      await api.post(`/candidate-docs/admin/${letterId}/request-replacement`, { item_id: itemId, reason });
      toast.success("Replacement request emailed from HR Department — reason recorded in the timeline");
      await load();
    } catch (err) { toast.error(fmtErr(err?.response?.data?.detail)); throw err; }
  };

  const [editing, setEditing] = useState(null);
  const saveCorrection = async (itemId) => {
    setBusy(itemId + "correct");
    try {
      await api.post(`/candidate-docs/admin/${letterId}/correct-text`, { item_id: itemId, value: editing?.value || "" });
      toast.success("Field corrected");
      setEditing(null);
      await load();
    } catch (err) { toast.error(fmtErr(err?.response?.data?.detail)); } finally { setBusy(""); }
  };
  const rejectText = async (itemId, reason) => {
    try {
      const { data: r } = await api.post(`/candidate-docs/admin/${letterId}/reject-text`, { item_id: itemId, reason });
      toast.success(`Field rejected — candidate notified by email${r.telegram?.ok ? ` · CEO informed on Telegram (${r.telegram.sent.length} chats)` : ""}`);
      if (r.telegram && !r.telegram.ok) toast.warning("Telegram confirmation NOT delivered to the CEO — check CEO_TELEGRAM_CHAT_IDS.", { duration: 9000 });
      await load();
    } catch (err) { toast.error(fmtErr(err?.response?.data?.detail)); throw err; }
  };

  const rejectDoc = async (itemId, reason) => {
    try {
      const { data: r } = await api.post(`/candidate-docs/admin/${letterId}/reject`, { item_id: itemId, reason });
      toast.success(`Document rejected — candidate notified by email${r.telegram?.ok ? ` · CEO informed on Telegram (${r.telegram.sent.length} chats)` : ""}`);
      if (r.telegram && !r.telegram.ok) toast.warning("Telegram confirmation NOT delivered to the CEO — check CEO_TELEGRAM_CHAT_IDS.", { duration: 9000 });
      await load();
    } catch (err) { toast.error(fmtErr(err?.response?.data?.detail)); throw err; }
  };

  const downloadAll = async () => {
    setBusy("zip");
    try {
      const res = await api.get(`/candidate-docs/admin/${letterId}/download-all`, { responseType: "blob" });
      const name = (data?.employee_name || "Candidate").replace(/[^A-Za-z0-9]/g, "") || "Candidate";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${name}_Documents.zip`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      const d = err?.response?.data;
      if (d instanceof Blob) { const t = await d.text(); try { toast.error(JSON.parse(t).detail); } catch { toast.error("No documents to download yet."); } }
      else toast.error(fmtErr(d?.detail));
    } finally { setBusy(""); }
  };

  return (
    <div data-lenis-prevent className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="checklist-modal">
      <div className="bg-nx-navy2 border border-white/15 w-full max-w-3xl max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-2 px-4 sm:px-5 py-3 border-b border-white/10 sticky top-0 bg-nx-navy2 z-10">
          <span className="text-sm text-white flex items-center gap-2 min-w-0"><FolderOpen size={16} className="text-nx-goldlight shrink-0" /> <span className="truncate">Document Checklist{data ? ` — ${data.employee_name}` : ""}</span></span>
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            {isCeo && data && data.stage !== "cancelled" && data.review?.status !== "cancelled" && <RequestMissingButton api={api} letterId={letterId} employeeName={data.employee_name} onDone={load} />}
            {isCeo && data && <ReviewActions api={api} letterId={letterId} review={data.review} letter={data} onDone={load} />}
            <button onClick={downloadAll} disabled={busy === "zip"} className="flex items-center gap-1.5 text-[11px] tracking-[0.12em] uppercase px-2.5 sm:px-3 py-1.5 border border-nx-gold/50 text-nx-goldlight hover:bg-nx-gold/10 disabled:opacity-50" data-testid="checklist-download-all">
              {busy === "zip" ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} <span className="hidden sm:inline">Download All</span>
            </button>
            <button onClick={() => window.print()} className="flex items-center gap-1.5 text-[11px] tracking-[0.12em] uppercase px-2.5 sm:px-3 py-1.5 border border-white/15 text-slate-300 hover:text-white" data-testid="checklist-print"><Printer size={12} /> <span className="hidden sm:inline">Print</span></button>
            <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-white" data-testid="checklist-close"><X size={16} /></button>
          </div>
        </div>

        {!data ? (
          <p className="text-slate-400 text-sm text-center py-10">Loading…</p>
        ) : (
          <div className="p-5" data-testid="checklist-body">
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-[11px] text-slate-400 border border-white/10 p-3 mb-4">
              <span>{data.designation} · {data.department}</span>
              <span>Joins {fmtDate(data.joining_date)}</span>
              <span>Candidate submission: <b className={data.candidate_status === "submitted" ? "text-emerald-400" : "text-amber-400"}>{data.candidate_status === "submitted" ? `Submitted ${fmtDate(data.submitted_at)}` : "Pending"}</b></span>
              {data.exempt && <span className="text-nx-goldlight">Role exempt from mandatory uploads</span>}
              <ReviewBadge review={data.review} testId="checklist-review-badge" />
              {data.flow === "v2" && !data.review?.status && <StageBadge letter={data} testId="checklist-stage-badge" />}
            </div>
            {data.summary && (
              <p className="text-[11px] text-slate-300 border border-white/10 bg-white/[0.02] px-3 py-2 mb-4 flex flex-wrap gap-x-4 gap-y-1" data-testid="checklist-summary">
                <span>Total Documents Required: <b className="text-white">{data.summary.required}</b></span>
                <span>Uploaded: <b className="text-white">{data.summary.uploaded}</b></span>
                <span>Verified: <b className="text-emerald-300">{data.summary.verified}</b></span>
                <span>Pending: <b className="text-amber-300">{data.summary.pending}</b></span>
                <span>Rejected: <b className="text-red-300">{data.summary.rejected}</b></span>
                <span>Replacement Requested: <b className="text-amber-300">{data.summary.replacement_requested}</b></span>
              </p>
            )}

            {data.doc_request && (
              <div className={`text-[11px] border px-3 py-2 mb-4 flex flex-wrap items-center gap-x-4 gap-y-1 ${data.doc_request.status === "open" ? "border-amber-500/40 bg-amber-500/5 text-amber-100" : data.doc_request.status === "fulfilled" ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-100" : "border-red-500/40 bg-red-500/5 text-red-100"}`} data-testid="checklist-doc-request">
                <span className="inline-flex items-center gap-1.5 tracking-[0.12em] uppercase"><Send size={11} /> Document request · {data.doc_request.status}</span>
                <span>{data.doc_request.kind === "staff" ? "Joined-staff rule" : "Candidate rule"} · {data.doc_request.window}</span>
                {data.doc_request.status === "open" && <span className="inline-flex items-center gap-1"><Clock size={11} /> Link valid till {fmtDT(data.doc_request.expires_at)}</span>}
                {data.doc_request.pending_labels?.length > 0 && data.doc_request.status !== "fulfilled" && <span>Pending: <b>{data.doc_request.pending_labels.join(", ")}</b></span>}
                {data.doc_request.status === "fulfilled" && <span>All requested documents received {fmtDT(data.doc_request.fulfilled_at)}</span>}
                {data.doc_request.status === "expired" && <span>Not submitted within {data.doc_request.window} — CEO alerted</span>}
                <span className="text-slate-400">Sent to {data.doc_request.sent_to}</span>
              </div>
            )}

            {data.folders.map((f) => (
              <div key={f.folder} className="mb-4">
                <p className="text-[11px] tracking-[0.14em] uppercase text-nx-goldlight mb-2 flex flex-wrap items-center gap-2">{f.folder}{f.folder.startsWith("05") && <BankVerifiedPill v={data.bank_verified} testId="chk-bank-verified-pill" />}</p>
                <div className="space-y-1.5">
                  {f.items.map((it) => (
                    <div key={it.id} className="border border-white/10 bg-white/[0.02] px-3 py-2.5" data-testid={`chk-item-${it.id}`}>
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <p className="text-sm text-white">{it.label} <span className="text-[10px] text-slate-500">· {it.req}</span></p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className={`text-[10px] tracking-[0.1em] uppercase border px-1.5 py-0.5 ${STATUS_BADGE[it.status] || STATUS_BADGE.pending}`} data-testid={`chk-status-${it.id}`}>{it.status}</span>
                            {it.uploaded_at && <span className="text-[10px] text-slate-500">{fmtDate(it.uploaded_at)}</span>}
                            {it.by && it.by !== "candidate" && <span className="text-[10px] text-slate-500">by HR</span>}
                            {it.verified && <span className="text-[10px] text-emerald-400 flex items-center gap-1"><ShieldCheck size={11} /> Verified</span>}
                            <ReplacementNote at={it.replacement_requested_at} reason={it.replacement_reason} testId={`chk-replace-note-${it.id}`} />
                            <RejectedNote at={it.rejected_at} reason={it.rejected_reason} testId={`chk-reject-note-${it.id}`} />
                            {it.replaced_at && !it.replacement_requested_at && !it.rejected_at && <span className="text-[10px] text-sky-300">Re-uploaded {fmtDate(it.replaced_at)}</span>}
                            {it.match?.detected && <span className="text-[10px] text-slate-500">detected: {it.match.detected}</span>}
                            {it.locked && <span className="text-[10px] text-emerald-300 flex items-center gap-1" data-testid={`chk-locked-${it.id}`}><Lock size={10} /> Locked — verified in ERPNext</span>}
                            {it.requested_at && <span className="text-[10px] text-amber-300 flex items-center gap-1" data-testid={`chk-requested-note-${it.id}`}><Send size={10} /> Requested {fmtDT(it.requested_at)} · awaiting {it.type === "text" ? "details" : "upload"}</span>}
                          </div>
                          {it.kyc && <div className="mt-1.5"><KycBadge k={it.kyc} testId={`chk-kyc-${it.id}`} /></div>}
                          {it.type === "text" && it.value && <p className="text-[11px] text-slate-300 mt-1 whitespace-pre-wrap break-words">{it.value}</p>}
                          {it.type === "text" && it.corrected_at && !it.rejected_at && <p className="text-[10px] text-sky-300 mt-0.5">Corrected by HR {fmtDate(it.corrected_at)}</p>}
                          {editing?.id === it.id && (
                            <form onSubmit={(e) => { e.preventDefault(); saveCorrection(it.id); }} className="flex flex-wrap gap-2 mt-2" data-testid={`chk-correct-form-${it.id}`}>
                              <input value={editing.value} onChange={(e) => setEditing({ id: it.id, value: e.target.value })} autoFocus maxLength={1000}
                                className="flex-1 min-w-[200px] bg-white/[0.04] border border-nx-gold/50 text-sm text-white px-3 py-2 outline-none" data-testid={`chk-correct-input-${it.id}`} />
                              <button type="submit" disabled={busy === it.id + "correct"} className="text-[10px] uppercase px-3 py-2 bg-nx-gold text-nx-navy font-semibold disabled:opacity-60" data-testid={`chk-correct-save-${it.id}`}>{busy === it.id + "correct" ? "Saving…" : "Save"}</button>
                              <button type="button" onClick={() => setEditing(null)} className="text-[10px] uppercase px-3 py-2 border border-white/15 text-slate-300" data-testid={`chk-correct-cancel-${it.id}`}>Cancel</button>
                            </form>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5 w-full sm:w-auto sm:shrink-0">
                          {it.has_file && (
                            <button onClick={() => viewFile(it.id)} disabled={busy === it.id + "view"} className="flex items-center gap-1 text-[10px] uppercase px-2.5 py-1.5 border border-white/15 text-slate-300 hover:text-white" data-testid={`chk-view-${it.id}`}>
                              {busy === it.id + "view" ? <Loader2 size={11} className="animate-spin" /> : <Eye size={11} />} View
                            </button>
                          )}
                          {(it.type === "file" || it.type === "acceptance") && it.has_file && (
                            <ReplaceRequestButton label={it.label} testId={`chk-replace-${it.id}`} onConfirm={(reason) => requestReplacement(it.id, reason)} />
                          )}
                          {isCeo && it.type === "file" && !it.has_file && data.stage !== "cancelled" && data.review?.status !== "cancelled" && (
                            <RequestDocButton label={it.label} resend={!!it.requested_at} disabled={busy === it.id + "req"} testId={`chk-request-${it.id}`}
                              hint={`An HR Department email with this reason and a secure upload link (valid ${data.window}) goes to ${data.employee_name} immediately. Both CEO Telegram accounts are notified and the reason is recorded in the timeline.`}
                              onConfirm={(reason) => requestDoc(it.id, it.label, reason)} />
                          )}
                          {it.type === "file" && !it.has_file && (
                            <label className="flex items-center gap-1 text-[10px] uppercase px-2.5 py-1.5 border border-white/15 text-slate-300 hover:text-white cursor-pointer" data-testid={`chk-hrupload-${it.id}`}>
                              {busy === it.id + "up" ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />} Upload
                              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => { hrUpload(it.id, e.target.files?.[0]); e.target.value = ""; }} />
                            </label>
                          )}
                          {(it.status !== "pending") && (
                            <button onClick={() => verify(it.id, !it.verified)} disabled={busy === it.id + "verify"} className={`flex items-center gap-1 text-[10px] uppercase px-2.5 py-1.5 border transition-colors ${it.verified ? "border-emerald-500/50 text-emerald-300" : "border-nx-gold/50 text-nx-goldlight hover:bg-nx-gold/10"}`} data-testid={`chk-verify-${it.id}`}>
                              {busy === it.id + "verify" ? <Loader2 size={11} className="animate-spin" /> : it.verified ? <CheckCircle2 size={11} /> : <Circle size={11} />} {it.verified ? "Verified" : "Mark Verified"}
                            </button>
                          )}
                          {(it.type === "file" || it.type === "acceptance") && it.has_file && it.status !== "rejected" && (
                            <RejectRequestButton label={it.label} testId={`chk-reject-${it.id}`} onConfirm={(reason) => rejectDoc(it.id, reason)} />
                          )}
                          {it.type === "text" && !it.locked && (
                            <>
                              {isCeo && !it.value && data.stage !== "cancelled" && data.review?.status !== "cancelled" && (
                                <RequestDocButton label={it.label} resend={!!it.requested_at} disabled={busy === it.id + "req"} testId={`chk-request-${it.id}`}
                                  hint={`An HR Department email with this reason and a secure link to fill in these details (valid ${data.window}) goes to ${data.employee_name} immediately. Both CEO Telegram accounts are notified and the reason is recorded in the timeline.`}
                                  onConfirm={(reason) => requestDoc(it.id, it.label, reason)} />
                              )}
                              <button onClick={() => setEditing({ id: it.id, value: it.value || "" })} className="flex items-center gap-1 text-[10px] uppercase px-2.5 py-1.5 border border-sky-500/40 text-sky-200 hover:bg-sky-500/10 transition-colors" data-testid={`chk-correct-${it.id}`}>
                                <Pencil size={11} /> {it.value ? "Correct" : "Enter"}
                              </button>
                              {it.value && it.status !== "rejected" && <RejectRequestButton label={it.label} testId={`chk-reject-${it.id}`} onConfirm={(reason) => rejectText(it.id, reason)} />}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
